'use client';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import React from 'react';
import {
   Box,
   Flex,
   Table,
   Tbody,
   Td,
   Text,
   Tr,
   useColorModeValue,
   useDisclosure,
   Drawer,
   DrawerOverlay,
   DrawerContent,
   DrawerCloseButton,
   DrawerHeader,
   DrawerBody,
   useOutsideClick,
   Spinner,
   Input,
   GridItem,
   Button,
   Image,
   Thead,
   Th,
   Grid,
} from '@chakra-ui/react';
import Select, { components } from 'react-select';
import Card from 'components/card/Card';
import { SearchIcon } from '@chakra-ui/icons';
import { getShareCompanyOptions } from 'utils/util';
import { motion } from 'framer-motion';
import { debounce } from 'lodash';
import { LuRefreshCcw } from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';
import { getLogs } from '../../../../redux/actions/logs';
import { useUser } from 'contexts';
import { useGlobal } from 'contexts';
import PeriodDatePicker from 'components/RangePicker/PeriodDatePicker';
import JsonDifferenceModal from '../components/JsonDifferenceModal';
import back from '../../../../../src/assets/img/nfts/back.svg';
import DotIcon from '../../../../assets/img/nfts/dot.svg';
import { renderDynamicDropdown } from '../../../../components/ui/DynamicDropdown';
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from '@tanstack/react-table';
import { IoFilterOutline } from 'react-icons/io5';
import SalesPurchaseFilter from '../components/SalesPurchaseFilter';
import Loader from 'views/admin/default/Loader';

const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);
const PAGE_SIZE = 20;
const LOAD_MORE_THRESHOLD = 100;
const MIN_LOADING_TIME = 300;

const TransactionHistory = () => {
   // Theme colors
   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const backgroundColor = useColorModeValue('white', '#101F4A');
   const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');

   const logsData = useSelector((state) => state.logs?.list || []);
   const logsLoading = useSelector((state) => state.logs?.loading);
   const entryType = localStorage.getItem('Entry_type');
   const entryid = localStorage.getItem('Entry_id');

   const type = entryType
      ? entryType === 'saleinvoice'
         ? 'sales'
         : entryType === 'salereturn'
            ? 'salesReturn'
            : entryType === 'purchaseinvoice'
               ? 'Purchases'
               : 'purchaseReturn'
      : 'sales';

   // State
   const [allData, setAllData] = useState([]);
   const [displayData, setDisplayData] = useState([]);
   const [currentPage, setCurrentPage] = useState(1);
   const [isLoading, setIsLoading] = useState(false);
   const [hasInitialData, setHasInitialData] = useState(false);
   const [searchQuery, setSearchQuery] = useState('');
   const [expanded, setExpanded] = useState(false);
   const [showDiffModal, setShowDiffModal] = useState(false);
   const [selectedLog, setSelectedLog] = useState(null);
   const [companyFilter, setCompanyFilter] = useState([]);
   const [reportType, setReportType] = useState(['Add']);
   const [transactionType, setTransactionType] = useState([type]);
   const [lastFetchParams, setLastFetchParams] = useState(null);

   const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();

   // Refs
   const tableContainerRef = useRef(null);
   const wrapperRef = useRef();
   const abortControllerRef = useRef(null);
   localStorage.removeItem('Entry_type');

   // Context and utilities
   const dbName = localStorage.getItem('dbName');
   const { user } = useUser();
   const dispatch = useDispatch();
   const { isOpen, onOpen, onClose } = useDisclosure();
   const { companyList } = useGlobal();

   // Date range state
   const [dateRange, setDateRange] = useState(() => {
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 6);

      return {
         fromDate: sevenDaysAgo.toISOString().split('T')[0], // last 7th day
         toDate: today.toISOString().split('T')[0], // today
      };
   });

   // Options
   const reportTypeOptions = [
      { id: 'Update', label: 'Update' },
      { id: 'Add', label: 'Add' },
      { id: 'Delete', label: 'Delete' },
   ];

   const transactionsTypeOptions = [
      { id: 'sales', label: 'Sales' },
      { id: 'Purchases', label: 'Purchase' },
      { id: 'salesReturn', label: 'Sales-Return' },
      { id: 'purchaseReturn', label: 'Purchase-Return' },
      { id: 'salesChallan', label: 'sales Challan' },
      { id: 'purchaseChallan', label: 'purchase Challan' },
      { id: 'voucher', label: 'Voucher (cash/bank)' },
      { id: 'account', label: 'Account' },
      { id: 'item', label: 'Item' },
      { id: 'category', label: 'Category' },
      { id: 'brand', label: 'Brand' },
      { id: 'series', label: 'Series' },
      { id: 'itemGroup', label: 'Item Group' },
      { id: 'warehouse', label: 'Warehouse' },
      { id: 'unit', label: 'Unit' },
      { id: 'group', label: 'Group' },
      { id: 'daybook', label: 'Daybook' },
      { id: 'billTerm', label: 'Bill - Term' },
      { id: 'bankBranch', label: 'Bank Branch' },
      { id: 'transport', label: 'Transport' },
      { id: 'driver', label: 'Driver' },
      { id: 'vehicle', label: 'Vehicle' },
      { id: 'color', label: 'Color' },
      { id: 'style', label: 'Style' },
      { id: 'size', label: 'Size' },
      { id: 'hsnMain', label: 'HSN' },
      // { id: 'countries', label: 'Countries' },
      // { id: 'states', label: 'States' },
      // { id: 'city', label: 'City' },
      { id: 'area', label: 'Area' },
      { id: 'mergecode', label: 'Merge Code' },
      { id: 'issue', label: 'Issue' },
      { id: 'receive', label: 'Receive' },
      { id: 'crm', label: 'CRM' },
      { id: 'todo', label: 'To Do' },
   ];

   const displayColumns = useMemo(() => {
      const cols = [
         { key: 'tableId', label: 'Table ID', type: 'number' },
         { key: 'tableName', label: 'Table Name', type: 'string' },
      ];

      if (
         transactionType?.includes('sales') ||
         transactionType?.includes('Purchases') ||
         transactionType?.includes('salesReturn') ||
         transactionType?.includes('purchaseReturn') ||
         transactionType?.includes('issue') ||
         transactionType?.includes('receive') ||
         transactionType?.includes('voucher')
      ) {
         cols.push(
            { key: 'newJson', subkey: 'docno', label: 'Doc No', type: 'string' },
            { key: 'newJson', subkey: 'daybookname', label: 'Daybook Name', type: 'string' },
            { key: 'newJson', subkey: 'accountname', label: 'Account Name', type: 'string' },
         );
      }

      if (
         transactionType?.includes('sales') ||
         transactionType?.includes('Purchases') ||
         transactionType?.includes('salesReturn') ||
         transactionType?.includes('purchaseReturn') ||
         transactionType?.includes('issue') ||
         transactionType?.includes('receive')
      ) {
         cols.push(
            { key: 'newJson', subkey: 'lineitems', label: 'Item Name', type: 'string' },
            { key: 'newJson', subkey: 'lineitems', label: 'Serial No', type: 'string' },
         );
      }

      if (transactionType?.includes('voucher')) {
         cols.push({
            key: 'newJson',
            subkey: 'txnType',
            label: 'Voucher Type',
            type: 'string',
         });
      }

      cols.push(
         { key: 'entryType', label: 'Entry Type', type: 'string' },
         { key: 'devicePlatform', label: 'Device Platform', type: 'string' },
         { key: 'createdAt', label: 'Created At', type: 'datetime' },
         { key: 'createdby', label: 'Created By Name', type: 'string' },
      );

      return cols;
   }, [transactionType]);

   // Memoized values
   const companyOptions = useMemo(() => {
      return getShareCompanyOptions({ dbName, company: companyList });
   }, [companyList, dbName]);

   const lastcompanyOptions = useMemo(
      () =>
         companyOptions.map((company) => ({
            value: company.value,
            label: company.label,
            id: company.value,
            name: company.label,
         })),
      [companyOptions],
   );

   const companyIds = useMemo(
      () =>
         companyFilter.length > 0
            ? companyFilter
            : localStorage.getItem('companyId')
               ? [parseInt(localStorage.getItem('companyId'))]
               : [],
      [companyFilter],
   );

   // Calculate total pages and check if more data available
   const totalPages = Math.ceil(allData.length / PAGE_SIZE);
   const hasMoreData = currentPage < totalPages;

   // Initialize default values
   useEffect(() => {
      const storedCompanyId = localStorage.getItem('companyId');
      if (storedCompanyId && companyList.length > 0) {
         const companyId = parseInt(storedCompanyId);
         const matchingCompany = companyList.find((comp) => comp.id === companyId);
         if (matchingCompany) {
            setCompanyFilter([matchingCompany.id]);
         }
      }
   }, [companyList]);

   // Custom debounce hook
   const useDebounce = (value, delay) => {
      const [debouncedValue, setDebouncedValue] = useState(value);

      useEffect(() => {
         const handler = setTimeout(() => {
            setDebouncedValue(value);
         }, delay);

         return () => {
            clearTimeout(handler);
         };
      }, [value, delay]);

      return debouncedValue;
   };

   const debouncedSearchQuery = useDebounce(searchQuery, 500);

   // Check if fetch parameters have changed
   const shouldFetchData = useCallback(
      (currentParams) => {
         if (!lastFetchParams) return true;

         return Object.keys(currentParams).some((key) => currentParams[key] !== lastFetchParams[key]);
      },
      [lastFetchParams],
   );

   // Fetch data function with proper error handling and state management
   const fetchAllData = useCallback(async () => {
      // 🚫 remove the isLoading guard
      const toCsv = (v) => {
         if (v == null) return '';
         if (Array.isArray(v)) return v.join(',');
         if (typeof v === 'object') {
            if ('value' in v) return String(v.value);
            return Object.values(v).map(String).join(',');
         }
         return String(v);
      };

      const currentParams = {
         compIds: companyFilter.length > 0 ? companyFilter.join(',') : user?.user?.loginhistory?.companyId,
         ...(toCsv(reportType) && { entryTypes: toCsv(reportType) }),
         ...(toCsv(transactionType) && { transactionTypes: toCsv(transactionType) }),
         ...(entryid && { tableId: entryid }),
         fromDate: dateRange.fromDate,
         toDate: dateRange.toDate,
         search: debouncedSearchQuery.trim(),
      };

      localStorage.removeItem('Entry_id');

      if (!shouldFetchData(currentParams)) return;

      if (abortControllerRef.current) {
         abortControllerRef.current.abort(); // cancel old request
      }

      abortControllerRef.current = new AbortController();
      setIsLoading(true);
      setLastFetchParams(currentParams);
      const startTime = Date.now();

      try {
         await dispatch(getLogs(currentParams));
      } catch (error) {
         if (error.name === 'AbortError') {
            console.log('Previous request aborted');
         } else {
            console.error('Error fetching logs:', error);
         }
      } finally {
         const elapsed = Date.now() - startTime;
         if (elapsed < MIN_LOADING_TIME) {
            setTimeout(() => setIsLoading(false), MIN_LOADING_TIME - elapsed);
         } else {
            setIsLoading(false);
         }
      }
   }, [
      dispatch,
      user?.loginhistory?.companyId,
      dateRange.fromDate,
      dateRange.toDate,
      debouncedSearchQuery,
      reportType,
      transactionType,
      shouldFetchData,
      companyFilter,
   ]);

   // Effect to handle logsData from Redux store
   useEffect(() => {
      if (logsData && logsData.length > 0) {
         setAllData(logsData);
         setDisplayData(logsData.slice(0, PAGE_SIZE));
         setCurrentPage(1);
         setHasInitialData(true);
      } else if (logsData && logsData.length === 0 && hasInitialData) {
         setAllData([]);
         setDisplayData([]);
         setCurrentPage(1);
      }
   }, [logsData, hasInitialData]);

   // Initial load
   useEffect(() => {
      fetchAllData();
   }, []);

   // Effect to handle filter changes with debounce
   useEffect(() => {
      const timer = setTimeout(() => {
         fetchAllData();
      }, 300);

      return () => clearTimeout(timer);
   }, [debouncedSearchQuery, dateRange, reportType, transactionType, companyFilter]);

   // Scroll handler with debouncing
   const loadMoreData = useCallback(() => {
      if (!hasMoreData || isLoading) return;

      const startIndex = currentPage * PAGE_SIZE;
      const endIndex = startIndex + PAGE_SIZE;
      const newData = allData.slice(startIndex, endIndex);

      if (newData.length > 0) {
         setDisplayData((prev) => [...prev, ...newData]);
         setCurrentPage((prev) => prev + 1);
      }
   }, [allData, currentPage, hasMoreData, isLoading]);
   useEffect(() => {
      const container = tableContainerRef.current;
      if (!container) return;

      const handleScroll = debounce(() => {
         const { scrollTop, scrollHeight, clientHeight } = container;
         const isNearBottom = scrollHeight - (scrollTop + clientHeight) < LOAD_MORE_THRESHOLD;

         if (isNearBottom && hasMoreData && !isLoading) {
            loadMoreData();
         }
      }, 200);

      container.addEventListener('scroll', handleScroll);
      return () => {
         container.removeEventListener('scroll', handleScroll);
         handleScroll.cancel();
      };
   }, [loadMoreData, hasMoreData, isLoading]);

   // Load more data from existing allData (frontend pagination)

   // Table configuration
   const columnHelper = createColumnHelper();

   const getColumns = useCallback(() => {
      const cols = [
         columnHelper.accessor('tableId', {
            id: 'tableId',
            headerText: 'Table ID',
            header: () => <Text>Table ID</Text>,
            cell: (info) => info.getValue(),
         }),
         columnHelper.accessor('tableName', {
            id: 'tableName',
            headerText: 'Table Name',
            header: () => <Text>Table Name</Text>,
            cell: (info) => info.getValue(),
         }),
      ];

      if (
         transactionType?.includes('sales') ||
         transactionType?.includes('Purchases') ||
         transactionType?.includes('salesReturn') ||
         transactionType?.includes('purchaseReturn') ||
         transactionType?.includes('issue') ||
         transactionType?.includes('receive') ||
         transactionType?.includes('voucher')
      ) {
         cols.push(
            columnHelper.accessor('newJson.docno', {
               id: 'docNo',
               headerText: 'Doc No',
               header: () => <Text>Doc No</Text>,
               cell: (info) => info.getValue() || '-',
            }),
            columnHelper.accessor('newJson.daybookname', {
               id: 'daybookname',
               headerText: 'Daybook Name',
               header: () => <Text>Daybook Name</Text>,
               cell: (info) => info.getValue() || '-',
            }),
            columnHelper.accessor('newJson.accountname', {
               id: 'accountname',
               headerText: 'Account Name',
               header: () => <Text>Account Name</Text>,
               cell: (info) => info.getValue() || '-',
            }),
         );
      }

      // ✅ Conditional columns
      if (
         transactionType?.includes('sales') ||
         transactionType?.includes('Purchases') ||
         transactionType?.includes('salesReturn') ||
         transactionType?.includes('purchaseReturn') ||
         transactionType?.includes('issue') ||
         transactionType?.includes('receive')
      ) {
         cols.push(
            columnHelper.accessor('newJson.lineitem.itemName', {
               id: 'itemName',
               headerText: 'Item Name',
               header: () => <Text>Item Name</Text>,
               cell: (info) => info.getValue() || '-',
            }),
            columnHelper.accessor('newJson.lineitem.serialNo', {
               id: 'serialNo',
               headerText: 'Serial No',
               header: () => <Text>Serial No</Text>,
               cell: (info) => info.getValue() || '-',
            }),
         );
      }

      if (transactionType?.includes('voucher')) {
         cols.push(
            columnHelper.accessor('newJson.txnType', {
               id: 'txnType',
               headerText: 'Voucher Type',
               header: () => <Text>Voucher Type</Text>,
               cell: (info) => info.getValue() || '-',
            }),
         );
      }

      cols.push(
         columnHelper.accessor('entryType', {
            id: 'entryType',
            headerText: 'Entry Type',
            header: () => <Text>Entry Type</Text>,
            cell: (info) => info.getValue(),
         }),
         columnHelper.accessor('devicePlatform', {
            id: 'devicePlatform',
            headerText: 'Device Platform',
            header: () => <Text>Device Platform</Text>,
            cell: (info) => info.getValue(),
         }),
         columnHelper.accessor('createdAt', {
            id: 'createdAt',
            headerText: 'Created At',
            header: () => <Text>Created At</Text>,
            cell: (info) => new Date(info.getValue()).toLocaleString(),
         }),
         columnHelper.accessor('createdby', {
            id: 'createdby',
            headerText: 'Created By Name',
            header: () => <Text>Created By</Text>,
            cell: (info) => info.getValue(),
         }),
      );

      return cols;
   }, [columnHelper, transactionType]);

   const columns = useMemo(() => getColumns(), [getColumns]);

   const table = useReactTable({
      data: displayData,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   // Helper function to safely get nested values
   const getNestedValue = (obj, path) => {
      return path.split('.').reduce((current, key) => current?.[key], obj);
   };

   const formatValue = useCallback((value, type) => {
      if (value === null || value === undefined) return '-';

      switch (type) {
         case 'datetime':
            return new Date(value).toLocaleString();
         case 'number':
            return value.toString();
         case 'string':
         default:
            return value;
      }
   }, []);

   // Handlers
   const handleRefresh = () => {
      setHasInitialData(false);
      setLastFetchParams(null); // Force refresh by resetting last fetch params
      fetchAllData();
   };

   const handleDateRangeChange = useCallback((dates) => {
      if (!dates || dates.length !== 2) return;
      const [from, to] = dates.map((date) => (typeof date === 'string' ? date : date.toISOString().slice(0, 10)));
      setDateRange({ fromDate: from, toDate: to });
   }, []);

   const handleCompanyChange = useCallback((selectedOptions) => {
      setCompanyFilter(selectedOptions ? selectedOptions : []);
   }, []);

   const handleSearchChange = useCallback((e) => {
      setSearchQuery(e.target.value);
   }, []);

   const handleOpenModel = (logItem) => {
      setSelectedLog(logItem);
      setShowDiffModal(true);
   };

   useOutsideClick({
      ref: wrapperRef,
      handler: () => {
         if (searchQuery === '') setExpanded(false);
      },
   });

   // Render functions
   const renderReportContent = useCallback(() => {
      if (isLoading) {
         return (
            <Tr>
               <Td colSpan={displayColumns.length} textAlign="center" py="100px">
                  <Loader />
               </Td>
            </Tr>
         );
      }

      if (!displayData || displayData.length === 0) {
         return (
            <Tr>
               <Td colSpan={displayColumns.length} textAlign="center" py="50px">
                  <Grid justifyContent="center" alignItems="center" gap={4}>
                     <GridItem>
                        <Text>No logs data found</Text>
                     </GridItem>
                  </Grid>
               </Td>
            </Tr>
         );
      }

      const MemoizedTableRow = React.memo(
         ({
            displayColumns,
            data,
            dataindex,
            // value,
         }) => {
            const salespurchaseData =
               data.tableName === 'sales' ||
               data.tableName === 'purchases' ||
               data.tableName === 'salesReturn' ||
               data.tableName === 'purchaseReturn' ||
               data.tableName === 'issue' ||
               data.tableName === 'recive';
            const linelineType =
               data.tableName === 'sales'
                  ? 'salesLineDetail'
                  : data.tableName === 'purchases'
                     ? 'purchaseLineDetail'
                     : data.tableName === 'purchaseReturn'
                        ? 'purchaseReturnLineDetail'
                        : data.tableName === 'issue'
                           ? 'issueLineDetail'
                           : data.tableName === 'recive'
                              ? 'receiveLineDetail'
                              : 'salesReturnLineDetail';

            return (
               <>
                  {salespurchaseData ? (
                     data?.newJson?.lineitems?.map((linedata, lineIndex) => (
                        <Tr
                           key={`${dataindex}-${lineIndex}`}
                           bg={lineIndex % 2 === 0 ? 'white' : 'rgba(0, 63, 140, 0.05)'}
                           _dark={{ bg: lineIndex % 2 === 0 ? '#101F4A' : 'rgba(2, 14, 56, 0.7)' }}
                           _hover={{
                              bg: 'rgba(0, 63, 140, 0.1)',
                              _dark: { bg: 'rgba(2, 14, 56, 0.9)' },
                           }}
                           onClick={() => handleOpenModel(data)}
                           cursor="pointer">
                           {displayColumns.map((column, colIndex) => {
                              let cellValue;

                              if (column.subkey) {
                                 if (column.subkey === 'lineitems') {
                                    // Special handling for lineitems
                                    if (column.label === 'Serial No') {
                                       cellValue = (linedata?.[linelineType] ?? [])
                                          .map((sn) => sn?.serialNo)
                                          .join(', ');
                                    } else {
                                       cellValue = linedata?.itemName || '-';
                                    }
                                 } else {
                                    // Other nested keys
                                    cellValue = lineIndex === 0 ? data?.[column.key]?.[column.subkey] : '-';
                                 }
                              } else {
                                 if (column.type === 'datetime') {
                                    cellValue = lineIndex === 0 ? new Date(data?.[column.key]).toLocaleString() : '-';
                                 } else {
                                    cellValue = lineIndex === 0 ? data?.[column.key] : '-';
                                 }
                              }

                              return (
                                 <Td
                                    key={`${lineIndex}-${colIndex}`}
                                    p="8px"
                                    fontSize="14px"
                                    color={textColor}
                                    borderColor={borderColor}
                                    whiteSpace="nowrap"
                                    textOverflow="ellipsis"
                                    maxW="200px"
                                    overflow="hidden">
                                    {column.subkey === 'txnType' ? '-' : cellValue}
                                 </Td>
                              );
                           })}
                        </Tr>
                     ))
                  ) : (
                     <Tr
                        key={`${data.id}-${dataindex}`}
                        bg={dataindex % 2 === 0 ? 'white' : 'rgba(0, 63, 140, 0.05)'}
                        _dark={{ bg: dataindex % 2 === 0 ? '#101F4A' : 'rgba(2, 14, 56, 0.7)' }}
                        _hover={{
                           bg: 'rgba(0, 63, 140, 0.1)',
                           _dark: { bg: 'rgba(2, 14, 56, 0.9)' },
                        }}
                        onClick={() => handleOpenModel(data)}
                        cursor="pointer">
                        {displayColumns.map((column) => {
                           const value = getNestedValue(data, column.key);
                           return (
                              <Td
                                 key={column.key}
                                 p="8px"
                                 fontSize="14px"
                                 color={textColor}
                                 borderColor={borderColor}
                                 whiteSpace="nowrap"
                                 textOverflow="ellipsis"
                                 maxW="200px"
                                 overflow="hidden">
                                 {/* {column.key === 'newJson.txnType'
                                    ? data?.newJson?.txnType === 0
                                       ? 'Receive'
                                       : 'Payment'
                                    : formatValue(value, column.type)} */}
                                 {column.key !== 'newJson'
                                    ? column.key === 'createdAt'
                                       ? new Date(data?.[column.key]).toLocaleString()
                                       : data[column.key]
                                    : column.subkey === 'docno'
                                       ? data.docNo === '0'
                                          ? '-'
                                          : data.docNo
                                       : column.subkey === 'daybookname'
                                          ? data.newJson.daybookName
                                          : column.subkey === 'accountname'
                                             ? data.newJson.accountName
                                             : column.subkey === 'lineitems'
                                                ? '-'
                                                : column.subkey === 'txnType'
                                                   ? data?.newJson?.txnType
                                                      ? data?.newJson?.txnType === 0
                                                         ? 'Receive'
                                                         : 'Payment'
                                                      : '-'
                                                   : column.subkey}
                              </Td>
                           );
                        })}
                     </Tr>
                  )}
               </>
            );
         },
      );

      const salespurchase =
         transactionType?.includes('sales') ||
         transactionType?.includes('Purchases') ||
         transactionType?.includes('salesReturn') ||
         transactionType?.includes('purchaseReturn') ||
         transactionType?.includes('issue') ||
         transactionType?.includes('receive');

      const voucher = transactionType?.includes('voucher');

      return (
         <>
            {!salespurchase
               ? displayData.map((log, index) => (
                  <Tr
                     key={`${log.id}-${index}`}
                     bg={index % 2 === 0 ? 'white' : 'rgba(0, 63, 140, 0.05)'}
                     _dark={{ bg: index % 2 === 0 ? '#101F4A' : 'rgba(2, 14, 56, 0.7)' }}
                     _hover={{
                        bg: 'rgba(0, 63, 140, 0.1)',
                        _dark: { bg: 'rgba(2, 14, 56, 0.9)' },
                     }}
                     onClick={() => handleOpenModel(log)}
                     cursor="pointer">
                     {displayColumns.map((column) => {
                        const value = getNestedValue(log, column.key);
                        return (
                           <Td
                              key={column.key}
                              p="8px"
                              fontSize="14px"
                              color={textColor}
                              borderColor={borderColor}
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              maxW="200px"
                              overflow="hidden">
                              {log.tableName === 'voucher'
                                 ? column.key === 'newJson'
                                    ? column.subkey === 'txnType'
                                       ? log?.newJson.txnType === 0
                                          ? 'Receive'
                                          : 'Payment'
                                       : column.subkey === 'docno'
                                          ? log?.newJson?.docNumber
                                          : column.subkey === 'daybookname'
                                             ? log?.newJson?.daybookName
                                             : column.subkey === 'accountname'
                                                ? log?.newJson?.accountName
                                                : '-'
                                    : formatValue(value, column.type)
                                 : column.key === 'newJson'
                                    ? '-'
                                    : column.subkey === 'txnType'
                                       ? '-'
                                       : formatValue(value, column.type)}
                           </Td>
                        );
                     })}
                  </Tr>
               ))
               : displayData.map((log, index) => (
                  <MemoizedTableRow
                     key={`${index}-${index}`}
                     dataindex={index}
                     displayColumns={displayColumns}
                     data={log}
                  // value={value}
                  />
               ))}

            {hasMoreData && (
               <Tr>
                  <Td colSpan={displayColumns.length} textAlign="center" py="20px">
                     <Text fontSize="sm" color="gray.500">
                        Scroll down to load more...
                     </Text>
                  </Td>
               </Tr>
            )}
         </>
      );
   }, [
      isLoading,
      logsLoading,
      hasInitialData,
      displayData,
      displayColumns,
      textColor,
      borderColor,
      formatValue,
      hasMoreData,
      getNestedValue,
   ]);

   const bgColor = useColorModeValue('white', '#1A202C');
   // const textColor = useColorModeValue("black", "white");
   const placeholderColor = useColorModeValue('#666', '#AAA');
   const optionHoverBg = useColorModeValue('#F0F0F0', '#2D3748');
   const multiBg = useColorModeValue('#3182CE', '#90CDF4'); // blue shades
   const multiText = useColorModeValue('white', 'black');

   const customStyles = {
      control: (provided) => ({
         ...provided,
         backgroundColor: bgColor,
         borderColor: '#CBD5E0',
         minWidth: '180px',
         fontSize: '13px',
      }),
      singleValue: (provided) => ({
         ...provided,
         color: textColor,
      }),
      placeholder: (provided) => ({
         ...provided,
         color: placeholderColor,
      }),
      option: (provided, state) => ({
         ...provided,
         backgroundColor: state.isFocused ? optionHoverBg : state.isSelected ? multiBg : bgColor,
         color: state.isSelected ? multiText : textColor,
      }),
      multiValue: (provided) => ({
         ...provided,
         backgroundColor: multiBg,
         color: multiText,
         borderRadius: '6px',
         padding: '0 4px',
      }),
      multiValueLabel: (provided) => ({
         ...provided,
         color: multiText,
         fontWeight: 500,
      }),
      multiValueRemove: (provided) => ({
         ...provided,
         color: multiText,
         ':hover': {
            backgroundColor: '#E53E3E',
            color: 'white',
         },
      }),
   };

   const CustomValueContainer = ({ children, ...props }) => {
      const { getValue, hasValue } = props;
      const values = getValue();

      if (!hasValue || values.length === 0) {
         return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
      }

      const firstLabel = values[0]?.label;
      const extraCount = values.length - 1;

      return (
         <components.ValueContainer {...props}>
            {extraCount > 0 ? `${firstLabel} +${extraCount}` : firstLabel}
         </components.ValueContainer>
      );
   };

   return (
      <>
         <Flex pr="25px" mb="5px" justifyContent="space-between" align="center" mt="50px" overflow={'hidden'}>
            <Flex>
               <img
                  src={back}
                  alt="Go Back"
                  width="15px"
                  onClick={(e) => {
                     e.stopPropagation();
                     window.history.back();
                  }}
                  style={{ cursor: 'pointer' }}
               />
               <Text color={textColor} fontSize="15px" fontWeight="700" lineHeight="100%" ml={5}>
                  Transaction History
               </Text>
            </Flex>
         </Flex>

         <Box mt="15px" bg={backgroundColor} px={4} py={0} borderRadius="7px">
            <Flex alignItems="center" gap={3} justifyContent={'end'}>
               <div flex="1" mr={''} justifyContent="center">
                  <Box my={2} width="300px" fontSize={'13px'} fontWeight={500} display={{ base: 'none', xl: 'block' }}>
                     <GridItem className="seperator-width" mt={5}>
                        {renderDynamicDropdown(
                           'company',
                           'Company',
                           lastcompanyOptions,
                           {
                              value: companyFilter,
                              onChange: handleCompanyChange,
                           },
                           {},
                           textColor,
                           false,
                           'Select company',
                           true,
                           undefined,
                           true,
                        )}
                     </GridItem>
                  </Box>
               </div>
               <Box flex="1" display={{ base: 'none', xl: 'flex' }}>
                  <Flex alignItems="center" mt={3}>
                     <PeriodDatePicker
                        value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                        onChange={handleDateRangeChange}
                        defaultPeriod="last7Days"
                        style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                     />
                  </Flex>
                  <Flex alignItems="center" mt={3}></Flex>
                  <Box w={'400px'} display="flex">
                     <GridItem className="seperator-width" mt={5} w="45%" mr="5%">
                        {renderDynamicDropdown(
                           'transactionsType',
                           'Transaction Type',
                           transactionsTypeOptions,
                           {
                              value: transactionType,
                              onChange: (selectedOptions) => {
                                 if (!selectedOptions || selectedOptions.length === 0) {
                                    setTransactionType([]);
                                 } else {
                                    setTransactionType(selectedOptions);
                                 }
                              },
                           },
                           {},
                           textColor,
                           false,
                           'Select Transaction Type',
                           true,
                           undefined,
                           true,
                        )}
                     </GridItem>
                     <GridItem className="seperator-width" mt={5} w="45%">
                        {renderDynamicDropdown(
                           'reportType',
                           'Report Type',
                           reportTypeOptions,
                           {
                              value: reportType,
                              onChange: (selectedOptions) => {
                                 if (!selectedOptions || selectedOptions.length === 0) {
                                    setReportType([]);
                                 } else {
                                    setReportType(selectedOptions);
                                 }
                              },
                           },
                           {},
                           textColor,
                           false,
                           'Select Report Type',
                           true,
                           undefined,
                           true,
                        )}
                     </GridItem>
                     {/* <Box my={2} zIndex={3} fontSize={"13px"} fontWeight={500}>
                        <label>Transaction Type</label>
                        <Select
                           isMulti
                           options={transactionsTypeOptions}
                           value={transactionsTypeOptions.filter((option) =>
                              transactionType.includes(option.value)
                           )}
                           onChange={(selectedOptions) => {
                              if (!selectedOptions || selectedOptions.length === 0) {
                                 // all deselected → clear state
                                 setTransactionType([]);
                              } else {
                                 // set state to all selected option values
                                 setTransactionType(selectedOptions.map((opt) => opt.value));
                              }
                           }}
                           styles={customStyles}
                           placeholder="Select Transaction Type"
                           components={{ ValueContainer: CustomValueContainer }}
                        />
                     </Box> */}
                  </Box>

                  {/* <Box my={2} zIndex={3} fontSize={"13px"} fontWeight={500}>
                     <label>Report Type</label>
                     <Select
                        isMulti
                        options={reportTypeOptions}
                        value={reportTypeOptions.filter((option) =>
                           reportType?.includes(option.value)
                        )}
                        onChange={(selectedOptions) =>
                           setReportType(selectedOptions.map((opt) => opt.value))
                        }
                        styles={customStyles}
                        placeholder="Select Report Type"
                        components={{ ValueContainer: CustomValueContainer }}
                     />
                  </Box> */}
               </Box>
               <Flex>
                  <Box flex="1" mr={3}>
                     <MotionFlex
                        ref={wrapperRef}
                        align="center"
                        bg={bg}
                        borderRadius="full"
                        cursor="pointer"
                        px={expanded ? 3 : 0}
                        w={expanded ? '250px' : '40px'}
                        h="40px"
                        transition="all 0.3s ease"
                        onClick={() => setExpanded(true)}>
                        <MotionSearchIcon
                           color={icon}
                           animate={{ rotate: expanded ? 0 : 90 }}
                           transition={{ duration: 0.3 }}
                           style={{
                              marginRight: expanded ? '8px' : '10px',
                              marginLeft: expanded ? '0' : '10px',
                           }}
                        />
                        {expanded && (
                           <Input
                              variant="unstyled"
                              placeholder="Search..."
                              value={searchQuery}
                              onChange={handleSearchChange}
                              color={icon}
                              flex="1"
                           />
                        )}
                     </MotionFlex>
                  </Box>
                  <Button onClick={handleRefresh} _hover={{ bg: 'blue.50' }} mr={2}>
                     <LuRefreshCcw size={20} />
                  </Button>
                  {(transactionType?.includes('sales') ||
                     transactionType?.includes('Purchases') ||
                     transactionType?.includes('salesReturn') ||
                     transactionType?.includes('purchaseReturn') ||
                     transactionType?.includes('voucher') ||
                     transactionType?.includes('issue') ||
                     transactionType?.includes('receive')) && (
                        <Button cursor={'pointer'} onClick={onFilterOpen} mr={2}>
                           <IoFilterOutline size={22} />
                        </Button>
                     )}
               </Flex>
               <Box ml={''}>
                  <Flex display={{ base: 'block', xl: 'none' }}>
                     <Button variant="unstyled" onClick={onOpen}>
                        <Image src={DotIcon} alt="Menu" width="25px" />
                     </Button>
                  </Flex>
               </Box>

               <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
                  <DrawerOverlay />
                  <DrawerContent borderTopRadius="20px">
                     <DrawerCloseButton />
                     <DrawerHeader borderBottomWidth="1px">Filters</DrawerHeader>
                     <DrawerBody>
                        <GridItem>
                           {renderDynamicDropdown(
                              'company',
                              'Company',
                              lastcompanyOptions,
                              {
                                 value: companyFilter,
                                 onChange: handleCompanyChange,
                              },
                              {},
                              textColor,
                              false,
                              'Select company',
                              true,
                              undefined,
                              true,
                           )}
                        </GridItem>
                        <Box my={2} fontSize={'13px'} fontWeight={500}>
                           <label>Report Type</label>
                           <Select
                              options={reportTypeOptions}
                              value={reportTypeOptions.find((option) => option.value === reportType)}
                              onChange={(selectedOption) => setReportType(selectedOption.value)}
                              placeholder="Select Report Type"
                           />
                        </Box>
                        <Button colorScheme="green" width="100%" onClick={onClose} mt={4}>
                           Apply Filters
                        </Button>
                     </DrawerBody>
                  </DrawerContent>
               </Drawer>
            </Flex>
         </Box>

         <Grid overflow="auto">
            <Card mt="15px" borderRadius="7px">
               <Box
                  ref={tableContainerRef}
                  overflowX="auto"
                  className="custom-scrollbar"
                  position="relative"
                  minH={{ base: '60vh', md: '65vh', xl: '70vh' }}
                  maxH={{ base: '60vh', md: '65vh', xl: '70vh' }}>
                  <Table variant="simple" color="gray.500" mb="24px" mt="12px">
                     <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                           <Tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => {
                                 if (!header.column.getIsVisible()) return null;

                                 const columnId = header.column.id;
                                 const isNumericColumn = ['debit', 'credit'].includes(columnId);

                                 return (
                                    <Th
                                       borderTop="1px solid"
                                       key={header.id}
                                       colSpan={header.colSpan}
                                       borderColor={borderColor}
                                       cursor="pointer"
                                       p="2px"
                                       py="4px"
                                       fontSize="10px"
                                       _dark={{ bg: '#080238' }}
                                       fontWeight="600"
                                       bg={'#F5F5F5'}
                                       position="sticky"
                                       top="-1"
                                       zIndex="1"
                                       color={textColor}
                                       isNumeric={isNumericColumn}
                                       whiteSpace="nowrap"
                                       onClick={header.column.getToggleSortingHandler()}>
                                       <Flex
                                          justifyContent={isNumericColumn ? 'flex-end' : 'space-between'}
                                          align="center"
                                          fontSize={{ sm: '10px', lg: '12px' }}
                                          color={textColor}>
                                          {flexRender(header.column.columnDef.header, header.getContext())}
                                          {{
                                             asc: '↑',
                                             desc: '↓',
                                          }[header.column.getIsSorted()] ?? null}
                                       </Flex>
                                    </Th>
                                 );
                              })}
                           </Tr>
                        ))}
                     </Thead>
                     <Tbody>{renderReportContent()}</Tbody>
                  </Table>
               </Box>
            </Card>
         </Grid>

         {showDiffModal && selectedLog && (
            <JsonDifferenceModal
               logData={selectedLog}
               isOpen={showDiffModal}
               onClose={() => {
                  setShowDiffModal(false);
                  setSelectedLog(null);
               }}
            />
         )}

         <SalesPurchaseFilter
            isOpen={isFilterOpen}
            onClose={onFilterClose}
            companyIds={companyIds}
            fromDate={dateRange.fromDate}
            toDate={dateRange.toDate}
            type={reportType}
            transactionType={transactionType}
            entityType={'transactionHistory'}
         />
      </>
   );
};

export default TransactionHistory;
